<template>
  <div class="details-panel d-flex flex-column h-100">
    <div class="p-2 border-bottom">
      <strong>{{ $t('whatsapp.details.title') }}</strong>
    </div>

    <b-alert
      :show="!!store.detailsError"
      variant="danger"
      class="m-2 mb-0 py-1 px-2 small"
      dismissible
      @dismissed="store.setDetailsError(null)"
    >
      {{ store.detailsError }}
    </b-alert>

    <div class="flex-grow-1 overflow-auto p-2">
      <!-- ------------------------------------------------ contact link -->
      <section class="mb-3">
        <div class="small text-uppercase text-muted mb-1">
          {{ $t('whatsapp.details.contact') }}
        </div>

        <template v-if="conversation.contactId">
          <div class="d-flex align-items-center">
            <div class="flex-grow-1">
              <!-- Names come from the CRM; render as text, never as markup. -->
              <div>
                {{ conversation.contactName || conversation.contactId }}
              </div>
              <div class="small text-muted">{{ conversation.contactId }}</div>
            </div>
            <b-button
              size="sm"
              variant="outline-danger"
              :disabled="store.detailsBusy"
              @click="unlink"
            >
              {{ $t('whatsapp.details.unlink') }}
            </b-button>
          </div>

          <!-- The account the contact belongs to. Today every WhatsApp contact
               is a partner, so the link jumps straight into that partner's
               contact/jobs view (search + openModal, the same deep link the
               jobs table uses). Opens in a new tab so the thread stays put. -->
          <div
            v-if="conversation.accountName || conversation.accountId"
            class="small mt-1"
          >
            <span class="text-muted">
              {{ $t('whatsapp.details.account') }}:
            </span>
            {{ conversation.accountName || conversation.accountId }}
            <a
              v-if="conversation.accountId"
              :href="partnerLink"
              target="_blank"
              rel="noopener"
              class="ml-1"
            >
              {{ $t('whatsapp.details.open-partner') }}
            </a>
          </div>
        </template>

        <template v-else>
          <div class="small text-muted mb-1">
            {{ $t('whatsapp.details.no-contact') }}
          </div>
          <b-button
            v-if="!store.suggestionsLoaded"
            size="sm"
            variant="outline-primary"
            :disabled="store.detailsBusy"
            @click="store.fetchSuggestions(conversation.id)"
          >
            {{ $t('whatsapp.details.find-contact') }}
          </b-button>
          <template v-else>
            <div
              v-if="!store.suggestions.length"
              class="small text-muted font-italic"
            >
              {{ $t('whatsapp.details.no-suggestions') }}
            </div>
            <div
              v-for="suggestion in store.suggestions"
              :key="suggestion.contactId"
              class="border rounded p-1 mb-1 d-flex align-items-center"
            >
              <div class="flex-grow-1 mr-1">
                <div class="small">
                  {{ suggestion.name || suggestion.contactId }}
                  <b-badge
                    :variant="
                      suggestion.confidence === 'exact' ? 'success' : 'warning'
                    "
                  >
                    {{
                      $t(`whatsapp.details.confidence.${suggestion.confidence}`)
                    }}
                  </b-badge>
                </div>
                <div class="small text-muted">
                  {{ suggestion.phone || suggestion.mobilePhone }}
                  <span v-if="suggestion.email">· {{ suggestion.email }}</span>
                </div>
              </div>
              <b-button
                size="sm"
                variant="outline-primary"
                :disabled="store.detailsBusy"
                @click="link(suggestion.contactId)"
              >
                {{ $t('whatsapp.details.link') }}
              </b-button>
            </div>
          </template>
        </template>
      </section>

      <!-- ------------------------------------------------------- jobs -->
      <section class="mb-3">
        <div class="small text-uppercase text-muted mb-1">
          {{ $t('whatsapp.details.jobs') }}
        </div>

        <div
          v-for="jobLink in store.jobLinks"
          :key="jobLink.id"
          class="border rounded p-1 mb-1 d-flex align-items-center"
        >
          <div class="flex-grow-1 mr-1 small">
            <div>{{ jobLink.jobName || jobLink.jobApiId }}</div>
            <div v-if="jobLink.jobStatus" class="text-muted">
              {{ jobLink.jobStatus }}
            </div>
          </div>
          <b-button
            size="sm"
            variant="outline-danger"
            :disabled="store.detailsBusy"
            @click="removeJob(jobLink.jobApiId)"
          >
            ×
          </b-button>
        </div>

        <b-form class="d-flex" @submit.prevent="addJob">
          <b-form-input
            v-model="jobRef"
            size="sm"
            class="mr-1"
            :placeholder="$t('whatsapp.details.job-placeholder')"
            :disabled="store.detailsBusy"
          />
          <b-button
            size="sm"
            type="submit"
            variant="outline-primary"
            :disabled="store.detailsBusy || !jobRef.trim()"
          >
            {{ $t('whatsapp.details.link') }}
          </b-button>
        </b-form>
      </section>

      <!-- ------------------------------------------------------ notes -->
      <section>
        <div class="small text-uppercase text-muted mb-1">
          {{ $t('whatsapp.details.notes') }}
        </div>

        <div
          v-for="note in store.notes"
          :key="note.id"
          class="border rounded p-1 mb-1"
        >
          <div class="small text-muted">
            {{ note.authorName || $t('whatsapp.details.unknown-author') }}
            · {{ note.createdAt | moment('LLL') }}
          </div>
          <!-- Staff-written, but still text — never v-html. -->
          <div class="small note-body">{{ note.body }}</div>
        </div>

        <b-form @submit.prevent="submitNote">
          <b-form-textarea
            v-model="noteDraft"
            size="sm"
            rows="2"
            max-rows="6"
            :placeholder="$t('whatsapp.details.note-placeholder')"
            :disabled="store.detailsBusy"
            class="mb-1"
          />
          <b-button
            size="sm"
            type="submit"
            variant="outline-primary"
            :disabled="store.detailsBusy || !noteDraft.trim()"
          >
            {{ $t('whatsapp.details.add-note') }}
          </b-button>
        </b-form>
      </section>
    </div>
  </div>
</template>

<script>
import { getModule } from 'vuex-module-decorators';
import { WhatsappModule } from '@/store/whatsapp.module';

/**
 * Context around a thread (M5): who the number belongs to, which jobs the
 * conversation is about, and internal staff notes. Everything here is
 * GID-internal — nothing in this panel ever reaches the customer.
 */
export default {
  name: 'ConversationDetailsPanel',
  props: {
    conversation: { type: Object, required: true },
  },
  data() {
    return { jobRef: '', noteDraft: '' };
  },
  computed: {
    store() {
      return getModule(WhatsappModule, this.$store);
    },
    /**
     * Deep link into the partner view: filter the partners list to this
     * account and pop its contact/jobs modal. Same contract as the jobs
     * table's partner link (`search` accepts the account id).
     */
    partnerLink() {
      return `/partners?search=${encodeURIComponent(
        this.conversation.accountId,
      )}&openModal=true`;
    },
  },
  created() {
    this.store.fetchDetails(this.conversation.id);
  },
  watch: {
    'conversation.id'(id) {
      if (id) this.store.fetchDetails(id);
      this.jobRef = '';
      this.noteDraft = '';
    },
  },
  methods: {
    link(contactId) {
      this.store.linkContact({
        conversationId: this.conversation.id,
        contactId,
      });
    },
    unlink() {
      this.store.unlinkContact(this.conversation.id);
    },
    async addJob() {
      const ok = await this.store.addJobLink({
        conversationId: this.conversation.id,
        jobRef: this.jobRef,
      });
      if (ok) this.jobRef = '';
    },
    async submitNote() {
      const ok = await this.store.addNote({
        conversationId: this.conversation.id,
        body: this.noteDraft,
      });
      if (ok) this.noteDraft = '';
    },
  },
};
</script>

<style scoped>
.note-body {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
</style>
